import * as React from "react"
import { ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react"

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./table"
import { cn } from "../../lib/utils"

export interface SimpleColumn<T> {
  key: string
  label: string
  headClassName?: string
  cellClassName?: string
  sortable?: boolean
  sortValue?: (row: T) => string | number | boolean | null | undefined
  render?: (row: T) => React.ReactNode
}

interface SimpleSortableTableProps<T> {
  columns: SimpleColumn<T>[]
  rows: T[]
  rowKey: (row: T, index: number) => string | number
  /**
   * Classe extra por linha, para destacar a linha inteira a partir do dado (ex.:
   * ticket acima do limite na coerência da iMile). Opcional: sem ela o
   * comportamento é o de antes, só o zebrado.
   */
  rowClassName?: (row: T, index: number) => string | undefined
}

export function SimpleSortableTable<T>({
  columns,
  rows,
  rowKey,
  rowClassName,
}: SimpleSortableTableProps<T>) {
  const [sortKey, setSortKey] = React.useState<string | null>(null)
  const [sortDir, setSortDir] = React.useState<"asc" | "desc">("asc")

  const sortedRows = React.useMemo(() => {
    if (!sortKey) return rows
    const column = columns.find((c) => c.key === sortKey)
    if (!column) return rows
    const getValue =
      column.sortValue ?? ((row: T) => (row as any)[sortKey])
    return [...rows].sort((a, b) => {
      const va = getValue(a)
      const vb = getValue(b)
      if (va == null && vb == null) return 0
      if (va == null) return 1
      if (vb == null) return -1
      let result: number
      if (typeof va === "number" && typeof vb === "number") {
        result = va - vb
      } else if (typeof va === "boolean" && typeof vb === "boolean") {
        result = Number(va) - Number(vb)
      } else {
        result = String(va).localeCompare(String(vb), "pt-BR", {
          numeric: true,
        })
      }
      return sortDir === "asc" ? result : -result
    })
  }, [rows, columns, sortKey, sortDir])

  const toggleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((dir) => (dir === "asc" ? "desc" : "asc"))
    } else {
      setSortKey(key)
      setSortDir("asc")
    }
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {columns.map((column) => {
            const sortable = column.sortable !== false
            const sorted = sortKey === column.key ? sortDir : null
            return (
              <TableHead key={column.key} className={column.headClassName}>
                {sortable ? (
                  <button
                    className="inline-flex items-center gap-1 font-medium cursor-pointer hover:text-foreground select-none"
                    onClick={() => toggleSort(column.key)}
                  >
                    {column.label}
                    {sorted === "asc" ? (
                      <ArrowUp className="h-4 w-4 text-primary" />
                    ) : sorted === "desc" ? (
                      <ArrowDown className="h-4 w-4 text-primary" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 opacity-40" />
                    )}
                  </button>
                ) : (
                  column.label
                )}
              </TableHead>
            )
          })}
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedRows.map((row, index) => (
          <TableRow
            key={rowKey(row, index)}
            className={cn(
              index % 2 === 1 && "bg-muted/20",
              rowClassName?.(row, index)
            )}
          >
            {columns.map((column) => (
              <TableCell key={column.key} className={column.cellClassName}>
                {column.render ? column.render(row) : (row as any)[column.key]}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
